import './RecurringSchedule.scss'

import { Badge, Col, Row } from "react-bootstrap";
import { SwipeAction, SwipeableList, SwipeableListItem, TrailingActions } from "react-swipeable-list";
import { faAdd, faCalendar, faClock, faHourglassHalf, faPlay, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useState } from "react";

import AppLoading from "../../../../commons/AppLoading";
import ConfirmModal from "../../../../commons/modal/ConfirmModal";
import CustomCard from "../../../../commons/cards/Card";
import EmptyState from "../../../../commons/EmptyState";
import FloatingButton from "../../../../commons/buttons/FloatingButton";
import FloatingVideoButton from "../../../../commons/buttons/FloatingVideoButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import HallAppToast from "../../../../commons/HallAppToast";
import MenuTitle from "../../../../commons/menu/MenuTitle";
import ModalConfiguration from "./ModalConfiguration";
import SchedulingAPI from "../../SchedulingAPI";
import { formatDate } from "../../../../commons/util/HallAppUtil";

const schedulerAPI = new SchedulingAPI();

function RecurringSchedule() {
    const [showModalConfig, setShowModalConfig] = useState(false);
    const [recurrings, setRecurrings] = useState([]);
    const [showModalDelete, setShowModalDelete] = useState(false);
    const [isLoadingModalDelete, setIsLoadingModalDelete] = useState(false);
    const [selectedRecurring, setSelectedRecurring] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        getAllRecurrings();
    }, []);

    const getAllRecurrings = async () => {
        try {
            setIsLoading(true);
            const response = await schedulerAPI.getAllRecurring();
            setRecurrings(response.data);
        } catch (_) {
            setRecurrings([]);
            HallAppToast.genericError();
        } finally {
            setIsLoading(false);
        }
    };

    const translateFrequency = (value) => {
        switch (value) {
            case "DAILY": return "Diária";
            case "WEEKLY": return "Semanal";
            case "FORTNIGHTLY": return "Quinzenal";
            case "MONTHLY": return "Mensal";
            case "CUSTOM": return "Personalizada";
            default: return "-";
        }
    };

    const translateDuration = (value, repetitionCount) => {
        switch (value) {
            case "INFINITE": return "Sem data final";
            case "REPETITION": return `${repetitionCount} repetições`;
            case "SPECIFIC_DATE": return `Até data específica`;
            default: return "-";
        }
    };

    const translateWeek = (day) => {
        const map = {
            SUNDAY: "Domingo",
            MONDAY: "Segunda",
            TUESDAY: "Terça",
            WEDNESDAY: "Quarta",
            THURSDAY: "Quinta",
            FRIDAY: "Sexta",
            SATURDAY: "Sábado",
        };
        return map[day] || day;
    };

    // Badge "tipo" de frequência (estilo)
    const frequencyVariant = (freq) => {
        switch (freq) {
            case "DAILY": return "primary";
            case "WEEKLY": return "success";
            case "FORTNIGHTLY": return "warning";
            case "MONTHLY": return "info";
            case "CUSTOM": return "secondary";
            default: return "dark";
        }
    };

    const deleteAction = (data) => (
        <TrailingActions>
            <SwipeAction
                destructive={false}
                onClick={() => {
                    setSelectedRecurring(data);
                    setShowModalDelete(true);
                }}
            >
                <div className="swipe">
                    <div className="swipe swipe-dlt">
                        <FontAwesomeIcon icon={faTrash} size="lg" />
                        <div>EXCLUIR</div>
                    </div>
                </div>
            </SwipeAction>
        </TrailingActions>
    );

    async function executeDeletion() {
        setIsLoadingModalDelete(true);
        try {
            await schedulerAPI.deleteRecurring(selectedRecurring.id);
            setShowModalDelete(false);
            setSelectedRecurring({});
            getAllRecurrings();
        } catch (_) {
            HallAppToast.genericError();
        } finally {
            setIsLoadingModalDelete(false);
        }
    }

    return (
        <>
            <div className="container">
                <MenuTitle title="Agendamento Recorrente" showArrowInfo={false} />

                {isLoading ? (
                    <AppLoading />
                ) : (
                    <Row className="g-3" style={{ margin: 0, width: "100%" }}>
                        {recurrings.length === 0 ? (
                            <EmptyState
                                title="Nenhum agendamento recorrente"
                                description="Crie uma recorrência para reservar horários repetidos (semanal, quinzenal ou mensal). Escolha o profissional, o dia/horário e a duração."
                                icon={<FontAwesomeIcon icon={faCalendar} />}
                                actionLabel="Criar recorrência"
                                onAction={() => setShowModalConfig(true)}
                            />
                        ) : (
                            recurrings.map((data) => {
                                const showWeeks =
                                    (data.frequency === "CUSTOM" || data.frequency === "WEEKLY" || data.frequency === "FORTNIGHTLY") &&
                                    Array.isArray(data.weeks) &&
                                    data.weeks.length > 0;

                                const hasServices = Array.isArray(data.services) && data.services.length > 0;

                                return (
                                    <Col xs={12} sm={6} md={4} lg={4} key={data.id} className="mb-3">
                                        <SwipeableList style={{ width: "100%" }}>
                                            <SwipeableListItem
                                                trailingActions={deleteAction(data)}
                                                key={data.id}
                                                fullSwipe={false}
                                            >
                                                <div style={{ width: "100%" }}>
                                                    <CustomCard>
                                                        {/* Header */}
                                                        <div className="d-flex align-items-start justify-content-between gap-2">
                                                            <div style={{ minWidth: 0 }}>
                                                                <div className="fw-semibold" style={{ fontSize: 16, lineHeight: "20px" }}>
                                                                    {data.customerName}
                                                                </div>
                                                                <div className="text-muted" style={{ fontSize: 12 }}>
                                                                    Recorrência ativa
                                                                </div>
                                                            </div>

                                                            <Badge bg={frequencyVariant(data.frequency)} pill>
                                                                {translateFrequency(data.frequency)}
                                                            </Badge>
                                                        </div>

                                                        <hr className="my-3" />

                                                        {/* Infos principais */}
                                                        <div className="d-flex flex-column gap-2">
                                                            <div className="d-flex align-items-center justify-content-between">
                                                                <div className="text-muted d-flex align-items-center gap-2" style={{ fontSize: 13 }}>
                                                                    <FontAwesomeIcon icon={faClock} />
                                                                    <span>Horário</span>
                                                                </div>
                                                                <div className="fw-semibold" style={{ fontSize: 13 }}>
                                                                    {data.hours}
                                                                </div>
                                                            </div>

                                                            <div className="d-flex align-items-center justify-content-between">
                                                                <div className="text-muted d-flex align-items-center gap-2" style={{ fontSize: 13 }}>
                                                                    <FontAwesomeIcon icon={faPlay} />
                                                                    <span>Início</span>
                                                                </div>
                                                                <div className="fw-semibold" style={{ fontSize: 13 }}>
                                                                    {formatDate(data.startDate)}
                                                                </div>
                                                            </div>

                                                            <div className="d-flex align-items-center justify-content-between">
                                                                <div className="text-muted d-flex align-items-center gap-2" style={{ fontSize: 13 }}>
                                                                    <FontAwesomeIcon icon={faHourglassHalf} />
                                                                    <span>Duração</span>
                                                                </div>
                                                                <div className="fw-semibold" style={{ fontSize: 13, textAlign: "right" }}>
                                                                    {translateDuration(data.durationRecurrence, data.duration)}
                                                                </div>
                                                            </div>

                                                            {data.durationRecurrence === "SPECIFIC_DATE" && data.specificDate && (
                                                                <div className="d-flex align-items-center justify-content-between">
                                                                    <div className="text-muted d-flex align-items-center gap-2" style={{ fontSize: 13 }}>
                                                                        <FontAwesomeIcon icon={faCalendar} />
                                                                        <span>Até</span>
                                                                    </div>
                                                                    <div className="fw-semibold" style={{ fontSize: 13 }}>
                                                                        {formatDate(data.specificDate)}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Dias da semana */}
                                                        {showWeeks && (
                                                            <>
                                                                <hr className="my-3" />
                                                                <div className="text-muted mb-2" style={{ fontSize: 12 }}>
                                                                    Dias da semana
                                                                </div>
                                                                <div className="d-flex flex-wrap gap-2">
                                                                    {data.weeks.map((w) => (
                                                                        <span
                                                                            key={w}
                                                                            className="px-2 py-1 rounded-pill"
                                                                            style={{
                                                                                fontSize: 12,
                                                                                background: "#f1f3ff",
                                                                                color: "#2f2a7a",
                                                                                border: "1px solid #e3e6ff",
                                                                            }}
                                                                        >
                                                                            {translateWeek(w)}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </>
                                                        )}

                                                        {/* Serviços */}
                                                        {hasServices && (
                                                            <>
                                                                <hr className="my-3" />
                                                                <div className="text-muted mb-2" style={{ fontSize: 12 }}>
                                                                    Serviços
                                                                </div>

                                                                <div className="d-flex flex-column gap-2">
                                                                    {data.services.map((s, idx) => (
                                                                        <div
                                                                            key={idx}
                                                                            className="d-flex align-items-start justify-content-between gap-2"
                                                                            style={{
                                                                                padding: "10px 12px",
                                                                                borderRadius: 12,
                                                                                background: "#fafafa",
                                                                                border: "1px solid #eee",
                                                                            }}
                                                                        >
                                                                            <div style={{ fontSize: 13, minWidth: 0 }}>
                                                                                <div className="fw-semibold" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                                                    {s.serviceName}
                                                                                </div>
                                                                                <div className="text-muted" style={{ fontSize: 12 }}>
                                                                                    com {s.employeeName}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </>
                                                        )}

                                                        {/* Hint swipe (mobile friendly) */}
                                                        <div className="text-muted mt-3" style={{ fontSize: 11 }}>
                                                            Dica: deslize para a esquerda para excluir
                                                        </div>
                                                    </CustomCard>
                                                </div>

                                            </SwipeableListItem>
                                        </SwipeableList>
                                    </Col>
                                );
                            })
                        )}
                        <div style={{ height: "calc(50px + 72px)" }}></div>
                    </Row>
                )}
            </div>

            <FloatingButton
                icon={faAdd}
                onClick={() => setShowModalConfig(true)}
            />

            {showModalDelete && (
                <ConfirmModal
                    show={showModalDelete}
                    title="Excluir"
                    isLoading={isLoadingModalDelete}
                    confirm={executeDeletion}
                    closeModal={() => {
                        setShowModalDelete(false);
                        setSelectedRecurring({});
                    }}
                >
                    Deseja remover o agendamento recorrente do(a) cliente{" "}
                    <span style={{ fontWeight: "bold" }}>{selectedRecurring.customerName}</span>?
                </ConfirmModal>
            )}

            {showModalConfig && (
                <ModalConfiguration
                    show={showModalConfig}
                    getAllRecurrings={getAllRecurrings}
                    closeShow={() => setShowModalConfig(false)}
                />
            )}

            <FloatingVideoButton youtubeUrl={"https://www.youtube.com/embed/JYAzsdqIv6M"} />
        </>
    );
}

export default RecurringSchedule;
